import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Col, Form, InputNumber, Row, Select, Space, Switch, Table, Tabs, Tag, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import evmAdminService from '../../../shared/services/evmAdminService';
import { adminPath } from '../../../shared/config/paths';
import { useTranslation } from '../../../shared/i18n';
import { currencySymbol, PLATFORM_CURRENCY } from '../../../shared/utils/money';

const { Text } = Typography;
const LEVEL_COLOR = { warn: 'warning', over: 'error' };
const RES_LABEL = { traffic: '流量', cpu: 'CPU', io: 'IO' };
const ok = (r) => r && r.status === 'success';

/** Traffic pack price (global row node_id 0, or per node). */
const PackConfig = ({ nodes }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [nodeId, setNodeId] = useState(0);
    const [saving, setSaving] = useState(false);
    const load = useCallback(async () => {
        const r = await evmAdminService.getTrafficPackConfig(nodeId);
        if (ok(r)) form.setFieldsValue({ ...r.data, enabled: !!r.data.switch });
    }, [nodeId, form]);
    useEffect(() => { load(); }, [load]);
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const r = await evmAdminService.saveTrafficPackConfig({ ...v, node_id: nodeId, enabled: v.enabled ? 1 : 0 });
        setSaving(false);
        if (ok(r)) message.success(t('Saved')); else message.error(r?.msg || t('Failed to save'));
    };
    return (
        <Card size="small" title={t('Traffic pack pricing')} extra={<Text type="secondary" style={{ fontSize: 12 }}>{t('Self-service add-on purchase must also be enabled')}</Text>}>
            <Form form={form} layout="inline" style={{ rowGap: 8 }}>
                <Form.Item label={t('Scope')}>
                    <Select style={{ width: 180 }} value={nodeId} onChange={setNodeId}
                        options={[{ value: 0, label: t('Global default') }, ...nodes.map((n) => ({ value: n.id, label: n.name }))]} />
                </Form.Item>
                <Form.Item name="enabled" label={t('Enabled')} valuePropName="checked"><Switch /></Form.Item>
                <Form.Item name="unit_gb" label={t('GB per pack')}><InputNumber min={1} /></Form.Item>
                <Form.Item name="unit_price" label={t('Price per pack')}><InputNumber min={0} step={1} addonAfter={currencySymbol(PLATFORM_CURRENCY)} /></Form.Item>
                <Form.Item name="max_qty" label={t('Max per purchase')}><InputNumber min={1} /></Form.Item>
                <Button type="primary" loading={saving} onClick={save}>{t('Save')}</Button>
            </Form>
        </Card>
    );
};

/** Per-GB traffic price (global + per availability zone) and the products sold hourly (global only). */
const BillingConfig = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const fill = useCallback((b) => {
        setCfg(b);
        form.setFieldsValue({
            t_enabled: b.traffic.enabled, unit_price: b.traffic.unit_price, cap: b.traffic.cap || null,
            zones: Object.fromEntries(b.traffic.zones.map((z) => [z.id, z.price])),
            products: b.payg.products, markup: b.payg.markup, prepay_hours: b.payg.prepay_hours, min_balance: b.payg.min_balance,
        });
    }, [form]);
    useEffect(() => {
        evmAdminService.getTrafficPackConfig(0).then((r) => { if (ok(r) && r.data.billing) fill(r.data.billing); });
    }, [fill]);
    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        const r = await evmAdminService.saveTrafficPackConfig({
            section: 'billing',
            traffic: { enabled: v.t_enabled ? 1 : 0, unit_price: v.unit_price, cap: v.cap || 0, zones: v.zones || {} },
            payg: { products: v.products || [], markup: v.markup, prepay_hours: v.prepay_hours, min_balance: v.min_balance },
        });
        setSaving(false);
        if (ok(r)) { message.success(t('Saved')); fill(r.data.billing); } else message.error(r?.msg || t('Failed to save'));
    };
    if (!cfg) return null;
    const cur = currencySymbol(PLATFORM_CURRENCY);
    return (
        <Card size="small" title={t('Traffic per GB · Hourly billing')} extra={<Button type="primary" loading={saving} onClick={save}>{t('Save')}</Button>}>
            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col xs={24} lg={12}>
                        <Space wrap align="end">
                            <Form.Item name="t_enabled" label={t('Enable')} valuePropName="checked"><Switch /></Form.Item>
                            <Form.Item name="unit_price" label={t('Price per GB')}><InputNumber min={0} step={0.001} precision={4} addonBefore={cur} /></Form.Item>
                            <Form.Item name="cap" label={t('Monthly cap per VM')} tooltip={t('Empty = no cap')}><InputNumber min={0} step={1} addonBefore={cur} /></Form.Item>
                        </Space>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{t('Availability zone')} · {t('empty = global price')}</Text>
                        <Space wrap>
                            {cfg.traffic.zones.map((z) => (
                                <Form.Item key={z.id} name={['zones', z.id]} label={z.name} style={{ marginBottom: 8 }}>
                                    <InputNumber min={0} step={0.001} precision={4} addonBefore={cur} style={{ width: 150 }} />
                                </Form.Item>
                            ))}
                        </Space>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Form.Item name="products" label={t('Products sold hourly')} tooltip={t('Hourly = monthly price × markup ÷ 720')}>
                            <Select mode="multiple" allowClear options={cfg.payg.choices.map((p) => ({ value: p.id, label: p.name, disabled: p.nat }))} />
                        </Form.Item>
                        <Space wrap>
                            <Form.Item name="markup" label={t('Markup')}><InputNumber min={1} max={5} step={0.05} /></Form.Item>
                            <Form.Item name="prepay_hours" label={t('Hours per prepayment')}><InputNumber min={1} max={168} /></Form.Item>
                            <Form.Item name="min_balance" label={t('Minimum balance')}><InputNumber min={0} step={1} addonBefore={cur} /></Form.Item>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
};

const QuotaReport = () => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await evmAdminService.getQuotaReport();
        setLoading(false);
        if (ok(r)) setData(r.data);
    }, []);
    useEffect(() => {
        load();
        evmAdminService.getNodesOverview().then((r) => { if (ok(r)) setNodes(Array.isArray(r.data) ? r.data : (r.data?.nodes || [])); });
    }, [load]);

    const vmLink = (v, r) => <Link to={adminPath(`/evm/vms/${r.id}`)}>{v || `#${r.id}`}</Link>;
    const top = (key, unit) => [
        { title: t('Instances'), dataIndex: 'name', render: vmLink },
        { title: t('User'), dataIndex: 'uid', width: 100 },
        { title: t('Node'), dataIndex: 'node_id', width: 90 },
        { title: unit, dataIndex: key, width: 140, sorter: (a, b) => a[key] - b[key] },
    ];

    return (
        <div>
            <PageHeader title={t('Quotas and overage')} extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />} />
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card size="small" title={t('Over-quota / restricted instances')}>
                    <Table size="small" rowKey="id" loading={loading} dataSource={data?.over || []} pagination={{ pageSize: 20 }} columns={[
                        { title: t('Instances'), dataIndex: 'name', render: vmLink },
                        { title: t('User'), dataIndex: 'uid', width: 100 },
                        { title: t('Node'), dataIndex: 'node_id', width: 90 },
                        { title: t('Status'), dataIndex: 'status', width: 100 },
                        {
                            title: t('Quota'),
                            dataIndex: 'levels',
                            render: (lv) => <Space size={4} wrap>{Object.entries(lv || {}).map(([k, v]) => <Tag key={k} color={LEVEL_COLOR[v]}>{`${RES_LABEL[k]} ${v === 'over' ? t('Over quota') : '≥80%'}`}</Tag>)}</Space>,
                        },
                        {
                            title: t('Manual restriction'),
                            dataIndex: 'manual',
                            render: (m) => (m ? <Space size={4} wrap>{m.cut_public && <Tag color="red">{t('Public network blocked')}</Tag>}<Text type="secondary">{m.reason}</Text></Space> : '-'),
                        },
                        { title: t('Exempt'), dataIndex: 'exempt', width: 70, render: (v) => (v ? <Tag>{t('Yes')}</Tag> : '') },
                    ]} />
                </Card>
                <Card size="small" title={t('Top usage (last 30 days)')}>
                    <Tabs items={[
                        { key: 'cpu', label: 'CPU', children: <Table size="small" rowKey="id" pagination={false} dataSource={data?.top_cpu || []} columns={top('cpu_core_hours', t('Core-hours'))} /> },
                        { key: 'io', label: t('Disk IO'), children: <Table size="small" rowKey="id" pagination={false} dataSource={data?.top_io || []} columns={top('io_gb', 'GB')} /> },
                        { key: 'traffic', label: t('Traffic'), children: <Table size="small" rowKey="id" pagination={false} dataSource={data?.top_traffic || []} columns={top('traffic_gb', 'GB')} /> },
                    ]} />
                </Card>
                <Row><Col span={24}><PackConfig nodes={nodes} /></Col></Row>
                <BillingConfig />
            </Space>
        </div>
    );
};

export default QuotaReport;
